/**
 * Affichage conditionne par les permissions : masquer une action, garder une
 * route. La verification qui fait foi reste celle de l'API.
 */
import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { LIBELLES_ROLE } from '@leads/shared';
import { MESSAGE_ACCES_REFUSE, surAccesRefuse } from '../lib/api';
import { usePermissions } from '../lib/permissions';
import type { Exigence } from '../lib/permissions';
import { classesBouton, useToast } from './ui';

export function Autorise({
  permission,
  children,
  sinon = null,
}: {
  permission: Exigence;
  children: ReactNode;
  /** Rendu a la place de `children` quand la permission manque. */
  sinon?: ReactNode;
}) {
  const { peut } = usePermissions();
  return <>{peut(permission) ? children : sinon}</>;
}

/**
 * Page d'un ecran refuse. On reste sur l'adresse demandee, dans la coquille :
 * pas de redirection (donc pas de boucle), et l'on explique pourquoi.
 */
export function AccesReserve() {
  const { role } = usePermissions();
  const motif = role
    ? `Votre rôle (${LIBELLES_ROLE[role]}) ne donne pas accès à cette page.`
    : 'Cette page demande des droits particuliers.';

  return (
    <section
      aria-labelledby="titre-acces-reserve"
      className="panneau flex flex-col items-center gap-2 px-6 py-16 text-center"
    >
      <span
        aria-hidden="true"
        className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-accent-50 text-accent-600 [&_svg]:size-6"
      >
        <Lock />
      </span>
      <h1 id="titre-acces-reserve" className="text-titre-page font-semibold tracking-titre text-encre">
        Accès réservé
      </h1>
      <p className="max-w-md text-sm text-encre-discrete">
        {`${motif} Si vous en avez besoin, demandez-le à un administrateur.`}
      </p>
      <Link to="/" className={`mt-3 ${classesBouton('primaire', 'md')}`}>
        Revenir à l'accueil
      </Link>
    </section>
  );
}

/** Garde de route : l'ecran demande, ou la page « Acces reserve ». */
export function RequiertPermission({ permission, children }: { permission: Exigence; children: ReactNode }) {
  const { peut } = usePermissions();
  return peut(permission) ? <>{children}</> : <AccesReserve />;
}

/** Des refus simultanes (requetes paralleles) ne produisent qu'une notification. */
const DELAI_REGROUPEMENT_MS = 3000;

/**
 * Toast pour tout 403 NON_AUTORISE : un filet de securite, puisque l'interface
 * masque deja les actions interdites. A monter une fois, sous FournisseurToasts.
 */
export function AlerteAccesRefuse(): null {
  const toast = useToast();
  const dernier = useRef(0);

  useEffect(
    () =>
      surAccesRefuse(() => {
        const maintenant = Date.now();
        if (maintenant - dernier.current < DELAI_REGROUPEMENT_MS) return;
        dernier.current = maintenant;
        toast.erreur(MESSAGE_ACCES_REFUSE, 'Action non autorisée');
      }),
    [toast],
  );

  return null;
}
